import React, { useState } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import { BasicModal } from "../BasicModal/BasicModal";
import { useWindowSize } from "@uidotdev/usehooks";
import {
	TRAVEL_KIT_MODAL,
	textFieldSx,
	modalContentSx,
	primaryButtonSx,
	secondaryButtonSx,
} from "./TravelKitModalStyles";

// Template106: Travel Kit - Itinerario Simple
const Template106 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	registerScrollContainer,
	isViewer = false,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const setScrollContainerRef = (domNode) => {
		if (typeof registerScrollContainer === "function") {
			registerScrollContainer(domNode);
		}
	};

	const pageData = page.data || {};
	const [days, setDays] = useState(() => {
		if (pageData.days?.length > 0) {
			return pageData.days;
		}
		return [
			{
				id: 1,
				date: "",
				label: "Día 1",
				activities: [
					{
						id: 1,
						time: "09:00",
						activity: "Llegada al aeropuerto",
						location: "",
						emoji: "✈️",
					},
					{
						id: 2,
						time: "12:00",
						activity: "Check-in hotel",
						location: "",
						emoji: "🏨",
					},
					{
						id: 3,
						time: "14:00",
						activity: "Almuerzo",
						location: "",
						emoji: "🍽️",
					},
					{
						id: 4,
						time: "16:00",
						activity: "Paseo por la ciudad",
						location: "",
						emoji: "🚶",
					},
				],
			},
			{
				id: 2,
				date: "",
				label: "Día 2",
				activities: [
					{
						id: 1,
						time: "10:00",
						activity: "Actividad principal",
						location: "",
						emoji: "⭐",
					},
				],
			},
			{
				id: 3,
				date: "",
				label: "Día 3",
				activities: [],
			},
		];
	});

	const [editModal, setEditModal] = useState(false);
	const [editDayModal, setEditDayModal] = useState(false);
	const [editingDay, setEditingDay] = useState(null);
	const [editingActivity, setEditingActivity] = useState(null);
	const [tempActivity, setTempActivity] = useState({
		time: "",
		activity: "",
		location: "",
		emoji: "",
	});
	const [tempDay, setTempDay] = useState({ label: "", date: "" });

	const updatePageData = (newDays) => {
		updatePage({
			...page,
			data: { ...pageData, days: newDays },
		});
	};

	const handleEditActivity = (dayIndex, activityIndex) => {
		setEditingDay(dayIndex);
		setEditingActivity(activityIndex);
		setTempActivity({ ...days[dayIndex].activities[activityIndex] });
		setEditModal(true);
	};

	const handleSaveActivity = () => {
		const newDays = [...days];
		if (editingActivity === -1) {
			// New activity
			const newId =
				Math.max(0, ...newDays[editingDay].activities.map((a) => a.id), 0) + 1;
			newDays[editingDay].activities.push({ id: newId, ...tempActivity });
			// Sort by time
			newDays[editingDay].activities.sort((a, b) =>
				a.time.localeCompare(b.time),
			);
		} else {
			newDays[editingDay].activities[editingActivity] = {
				...newDays[editingDay].activities[editingActivity],
				...tempActivity,
			};
		}
		setDays(newDays);
		updatePageData(newDays);
		setEditModal(false);
	};

	const handleAddActivity = (dayIndex) => {
		setEditingDay(dayIndex);
		setEditingActivity(-1);
		setTempActivity({ time: "", activity: "", location: "", emoji: "📍" });
		setEditModal(true);
	};

	const handleDeleteActivity = (dayIndex, activityIndex) => {
		const newDays = [...days];
		newDays[dayIndex].activities.splice(activityIndex, 1);
		setDays(newDays);
		updatePageData(newDays);
	};

	const handleEditDay = (dayIndex) => {
		setEditingDay(dayIndex);
		setTempDay({ label: days[dayIndex].label, date: days[dayIndex].date });
		setEditDayModal(true);
	};

	const handleSaveDay = () => {
		const newDays = [...days];
		newDays[editingDay] = { ...newDays[editingDay], ...tempDay };
		setDays(newDays);
		updatePageData(newDays);
		setEditDayModal(false);
	};

	const handleAddDay = () => {
		const newId = Math.max(0, ...days.map((d) => d.id)) + 1;
		const newDays = [
			...days,
			{
				id: newId,
				date: "",
				label: `Día ${newId}`,
				activities: [],
			},
		];
		setDays(newDays);
		updatePageData(newDays);
	};

	const handleDeleteDay = (dayIndex) => {
		const newDays = days.filter((_, i) => i !== dayIndex);
		setDays(newDays);
		updatePageData(newDays);
	};

	const formatDate = (dateStr) => {
		if (!dateStr) return "";
		const date = new Date(dateStr);
		return date.toLocaleDateString("es-ES", {
			weekday: "short",
			day: "numeric",
			month: "short",
		});
	};

	const emojiOptions = [
		"✈️",
		"🏨",
		"🍽️",
		"🚶",
		"⭐",
		"🎢",
		"🛍️",
		"📸",
		"🎭",
		"🏛️",
		"🌅",
		"🚌",
		"🚇",
		"☕",
		"🍜",
		"🎫",
	];

	const templateItemProps = {
		"data-template-item": true,
		className: "template-item",
	};

	const accentColor = headerBackgroundColor || "#E8B4BC";

	return (
		<Box
			{...templateItemProps}
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#FFF8F5",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* Header: washi strip + sticker Mi Itinerario */}
			<Box
				data-block-id="header"
				sx={{
					position: "sticky",
					top: 0,
					zIndex: 10,
					padding: isSmallScreen ? "12px 16px" : "16px 20px",
					backgroundColor: "rgb(248, 250, 252)",
					backgroundImage: `repeating-linear-gradient(
						100deg,
						transparent,
						transparent 6px,
						rgba(232, 180, 188, 0.3) 6px,
						rgba(232, 180, 188, 0.3) 9px
					)`,
					borderBottom: "3px dashed rgba(232, 180, 188, 0.5)",
					boxShadow: "0 4px 12px rgba(232, 180, 188, 0.2)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
					}}
				>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 1.5,
							backgroundColor: "rgba(255,255,255,0.95)",
							borderRadius: "24px",
							padding: "10px 18px",
							boxShadow:
								"0 3px 14px rgba(232, 180, 188, 0.35), 0 1px 3px rgba(0,0,0,0.06)",
							border: "2px solid rgba(232, 180, 188, 0.5)",
							transform: "rotate(-1.2deg)",
						}}
					>
						<CalendarMonthIcon sx={{ fontSize: 22, color: "#E8B4BC" }} />
						<Typography
							sx={{
								fontSize: "17px !important",
								fontWeight: "800 !important",
								color: "#5C3A45",
							}}
						>
							✿ Mi Itinerario ✿
						</Typography>
					</Box>
					<Typography
						sx={{
							backgroundColor: "rgba(255,255,255,0.9)",
							padding: "6px 12px",
							borderRadius: "14px",
							border: "2px dashed rgba(232, 180, 188, 0.4)",
							fontSize: "12px !important",
							color: "#5C3A45",
							fontWeight: 600,
						}}
					>
						{days.length} días
					</Typography>
				</Box>
			</Box>

			{/* Content */}
			<Box
				ref={setScrollContainerRef}
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					WebkitOverflowScrolling: "touch",
					padding: isSmallScreen ? "12px" : "20px",
				}}
			>
				{/* Days Timeline */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
					{days.map((day, dayIndex) => (
						<Box key={day.id} data-block-id={`text-${dayIndex + 1}`}>
							{/* Day Header - sticker tab */}
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									marginBottom: 2,
								}}
							>
								<Box
									{...templateItemProps}
									onClick={() => handleEditDay(dayIndex)}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1.5,
										cursor: "pointer",
										transform:
											dayIndex % 2 === 0 ? "rotate(-0.8deg)" : "rotate(0.8deg)",
										transition: "transform 0.2s",
										"&:hover": {
											opacity: 0.9,
											transform:
												dayIndex % 2 === 0
													? "rotate(-0.8deg) scale(1.02)"
													: "rotate(0.8deg) scale(1.02)",
										},
									}}
								>
									<Box
										sx={{
											width: 42,
											height: 42,
											borderRadius: "14px",
											backgroundColor: accentColor,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "#fff",
											fontWeight: 800,
											fontSize: "15px",
											boxShadow:
												"0 3px 12px rgba(232, 180, 188, 0.4), 0 1px 2px rgba(0,0,0,0.06)",
											border: "2px solid rgba(255,255,255,0.8)",
										}}
									>
										{dayIndex + 1}
									</Box>
									<Box>
										<Typography
											sx={{
												fontSize: "15px !important",
												fontWeight: "700 !important",
												color: "#1E293B",
											}}
										>
											{day.label}
										</Typography>
										{day.date && (
											<Typography
												sx={{
													fontSize: "12px !important",
													color: "#64748B",
												}}
											>
												{formatDate(day.date)}
											</Typography>
										)}
									</Box>
								</Box>
								<Box className="hide-in-viewer" sx={{ display: "flex", gap: 0.5 }}>
									<IconButton
										{...templateItemProps}
										onClick={() => !isViewer && handleEditDay(dayIndex)}
										size="small"
										sx={{ color: "#94A3B8" }}
									>
										<EditIcon sx={{ fontSize: 16, pointerEvents: "none" }} />
									</IconButton>
									<IconButton
										{...templateItemProps}
										onClick={() => !isViewer && handleDeleteDay(dayIndex)}
										size="small"
										sx={{ color: "#94A3B8" }}
									>
										<DeleteIcon sx={{ fontSize: 16, pointerEvents: "none" }} />
									</IconButton>
								</Box>
							</Box>

							{/* Activities - recorte con cinta lateral */}
							<Box
								sx={{
									transform:
										dayIndex % 2 === 0 ? "rotate(0.5deg)" : "rotate(-0.5deg)",
									marginLeft: isSmallScreen ? 0 : "24px",
								}}
							>
								<Box
									sx={{
										backgroundColor: "#fff",
										borderRadius: "20px",
										overflow: "hidden",
										boxShadow:
											"0 6px 24px rgba(232, 180, 188, 0.2), 0 2px 6px rgba(0,0,0,0.06)",
										border: "2px solid rgba(232, 180, 188, 0.3)",
										borderLeft: isSmallScreen
											? "2px solid rgba(232, 180, 188, 0.3)"
											: `5px solid ${accentColor}`,
									}}
								>
									{day.activities.length > 0 ? (
										day.activities.map((activity, activityIndex) => (
											<Box
												key={activity.id}
												sx={{
													display: "flex",
													alignItems: "flex-start",
													padding: "14px 16px",
													borderBottom:
														activityIndex < day.activities.length - 1
															? "1px solid #f1f5f9"
															: "none",
													gap: 2,
												}}
											>
												{/* Time */}
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 0.5,
														minWidth: "60px",
													}}
												>
													<AccessTimeIcon
														sx={{ fontSize: 14, color: "#94A3B8" }}
													/>
													<Typography
														sx={{
															fontSize: "13px !important",
															fontWeight: "600 !important",
															color: "#64748B",
														}}
													>
														{activity.time || "--:--"}
													</Typography>
												</Box>

												{/* Emoji */}
												<Box
													sx={{
														width: 32,
														height: 32,
														borderRadius: "8px",
														backgroundColor: `${accentColor}10`,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														fontSize: "16px",
														flexShrink: 0,
													}}
												>
													{activity.emoji || "📍"}
												</Box>

												{/* Content */}
												<Box
													{...templateItemProps}
													onClick={() =>
														!isViewer && handleEditActivity(dayIndex, activityIndex)
													}
													sx={{ flex: 1, cursor: isViewer ? "default" : "pointer" }}
												>
													<Typography
														sx={{
															fontSize: "14px !important",
															fontWeight: "600 !important",
															color: "#1E293B",
														}}
													>
														{activity.activity}
													</Typography>
													{activity.location && (
														<Box
															sx={{
																display: "flex",
																alignItems: "center",
																gap: 0.5,
																mt: 0.5,
															}}
														>
															<PlaceIcon
																sx={{ fontSize: 12, color: "#94A3B8" }}
															/>
															<Typography
																sx={{
																	fontSize: "12px !important",
																	color: "#64748B",
																}}
															>
																{activity.location}
															</Typography>
														</Box>
													)}
												</Box>

												{/* Delete */}
												<IconButton
													{...templateItemProps}
													className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
													onClick={() =>
														!isViewer && handleDeleteActivity(dayIndex, activityIndex)
													}
													size="small"
													sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }}
												>
													<DeleteIcon sx={{ fontSize: 14 }} />
												</IconButton>
											</Box>
										))
									) : (
										<Box
											sx={{
												padding: "20px",
												textAlign: "center",
												color: "#94A3B8",
											}}
										>
											<Typography sx={{ fontSize: "13px !important" }}>
												Sin actividades programadas
											</Typography>
										</Box>
									)}

									{/* Add Activity Button */}
									<Button
										{...templateItemProps}
										className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
										onClick={() => !isViewer && handleAddActivity(dayIndex)}
										startIcon={<AddIcon sx={{ fontSize: 16, pointerEvents: "none" }} />}
										sx={{
											width: "100%",
											textTransform: "none",
											fontSize: "13px",
											padding: "12px",
											borderRadius: 0,
											border: "none !important",
											borderTop: "1px solid #efefefef !important",
											color: "#94a2b8 !important",
											"&:hover": {
												backgroundColor: `${accentColor}08`,
											},
										}}
									>
										Añadir actividad
									</Button>
								</Box>
							</Box>
						</Box>
					))}

					{/* Add Day - sticker scrapbook */}
					<Box
						{...templateItemProps}
						className={[templateItemProps?.className, "hide-in-viewer"].filter(Boolean).join(" ")}
						onClick={() => !isViewer && handleAddDay()}
						sx={{
							backgroundColor: "#fff",
							borderRadius: "20px",
							border: "2px dashed #E8B4BC",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "24px",
							cursor: "pointer",
							transition: "all 0.2s",
							boxShadow: "0 4px 20px rgba(232, 180, 188, 0.15)",
							transform: "rotate(0.8deg)",
							"&:hover": {
								borderColor: accentColor,
								backgroundColor: "#FFE4EC",
								transform: "rotate(0.8deg) scale(1.02)",
							},
						}}
					>
						<AddIcon sx={{ fontSize: 22, color: "#B8959E", mr: 1 }} />
						<Typography
							sx={{
								color: "#5C3A45",
								fontSize: "14px !important",
								fontWeight: "600 !important",
							}}
						>
							✿ Añadir día
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Edit Activity Modal - diseño coherente con Template103/104 (oculto en viewer) */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={400}
				showModal={editModal}
				handleClose={() => setEditModal(false)}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						{editingActivity === -1 ? "Nueva actividad" : "Editar actividad"}
					</Typography>

					<TextField
						label="Hora"
						type="time"
						value={tempActivity.time}
						onChange={(e) =>
							setTempActivity({ ...tempActivity, time: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						InputLabelProps={{ shrink: true }}
						sx={textFieldSx}
					/>

					<TextField
						label="Actividad"
						value={tempActivity.activity}
						onChange={(e) =>
							setTempActivity({ ...tempActivity, activity: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						placeholder="Ej: Visitar el museo"
						sx={textFieldSx}
					/>

					<TextField
						label="Ubicación (opcional)"
						value={tempActivity.location}
						onChange={(e) =>
							setTempActivity({ ...tempActivity, location: e.target.value })
						}
						fullWidth
						size="small"
						margin="normal"
						placeholder="Ej: Centro histórico"
						sx={textFieldSx}
					/>

					<Typography
						sx={{
							fontSize: "13px !important",
							fontWeight: 600,
							color: TRAVEL_KIT_MODAL.textMuted,
							mt: 2,
							mb: 1.5,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Icono
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{emojiOptions.map((emoji) => (
							<Box
								key={emoji}
								onClick={() => setTempActivity({ ...tempActivity, emoji })}
								sx={{
									width: 36,
									height: 36,
									borderRadius: "8px",
									backgroundColor:
										tempActivity.emoji === emoji
											? "rgba(99, 102, 241, 0.12)"
											: TRAVEL_KIT_MODAL.bgInput,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "18px",
									cursor: "pointer",
									border:
										tempActivity.emoji === emoji
											? `3px solid ${TRAVEL_KIT_MODAL.primary}`
											: "3px solid transparent",
									transition: "transform 0.1s",
									"&:hover": { transform: "scale(1.1)" },
								}}
							>
								{emoji}
							</Box>
						))}
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditModal(false)}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveActivity}
							sx={primaryButtonSx}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>

			{/* Edit Day Modal - diseño coherente con Template103/104 */}
			<Box className="hide-in-viewer">
			<BasicModal
				width={400}
				showModal={editDayModal}
				handleClose={() => setEditDayModal(false)}
				contentSx={modalContentSx}
			>
				<Box sx={{ p: 3 }}>
					<Typography
						sx={{
							fontSize: "18px !important",
							fontWeight: 700,
							color: TRAVEL_KIT_MODAL.textPrimary,
							mb: 2,
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Editar día
					</Typography>

					<TextField
						label="Nombre del día"
						value={tempDay.label}
						onChange={(e) => setTempDay({ ...tempDay, label: e.target.value })}
						fullWidth
						size="small"
						margin="normal"
						placeholder="Ej: Día 1 - Llegada"
						sx={textFieldSx}
					/>

					<TextField
						label="Fecha"
						type="date"
						value={tempDay.date}
						onChange={(e) => setTempDay({ ...tempDay, date: e.target.value })}
						fullWidth
						size="small"
						margin="normal"
						InputLabelProps={{ shrink: true }}
						sx={textFieldSx}
					/>

					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 1.5,
							mt: 3,
						}}
					>
						<Button
							onClick={() => setEditDayModal(false)}
							sx={secondaryButtonSx}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveDay}
							sx={primaryButtonSx}
						>
							Guardar
						</Button>
					</Box>
				</Box>
			</BasicModal>
			</Box>
		</Box>
	);
};

Template106.thumbnail = "/images/templates/template-tk-itinerary.png";

export default Template106;
